/**
 * Text opacity authoring, for `setOpacity` on a Text object. `Text.opacity`
 * can be bound to a property, so each state or page shows its own; this is the
 * Text counterpart of `setMaterialFactorForState`.
 */

import type { SceneObject } from '../core/SceneObject';
import type { ValidationError } from '../scenedata/behaviordata';
import { boundPropertyName } from '../behaviors/tracebehavior';
import { Text } from '../behaviors/text';
import { Properties } from '../behaviors/properties';
import type { UndoCommand } from './undo/UndoCommand';
import { getUndoManager } from './undo/UndoManager';
import { BehaviorCommitCommand } from './undo/BehaviorCommitCommand';
import { AddBehaviorCommand } from './undo/AddBehaviorCommand';
import { CompositeCommand } from './undo/CompositeCommand';
import { dispatchBehaviorChanged } from './undo/editor-events';
import { freezeStatesForDeclaration, setStateValue } from './states/statesModel';
import { capturePageState, type PageEditing } from './states/pageCapture';
import { pageStateRefusal, routeOf, type Route } from './authoring/editRoute';
import {
    captureStateValue,
    editTargetState,
    nearestStateAncestor,
    seedMemberStatesFromGroup,
    stateEditRefused,
} from './states/statesActions';
import { findPropertyProvider } from './property-provider';
import {
    authorBoundValueInner,
    bindingDeclaration,
    queuePerObject,
    sameRoute,
    type BindCommandOptions,
} from './renderer-bindings';

const PROPERTY = 'textOpacity';

export interface SetTextOpacityOptions extends BindCommandOptions {
    /** Whether page editing is on, as for `setMaterialFactorForState`. */
    pageEditing?: PageEditing;
}

export interface SetTextOpacityResult {
    /** Empty on success, and on a write refused in Play or left behind by a change of route. */
    errors: ValidationError[];
    /** Where the value went: a state's or page's value, the bound property's base value, or the Text's own opacity. */
    target: 'state' | 'property' | 'text' | null;
    /** The property the opacity is bound to, when it is. */
    propertyName: string | null;
    /** The undo command for the whole edit; null when nothing was written. */
    command: UndoCommand | null;
    /** True when this call bound the opacity. */
    autoBound: boolean;
}

function nothingWritten(errors: ValidationError[] = []): SetTextOpacityResult {
    return { errors, target: null, propertyName: null, command: null, autoBound: false };
}

function refusal(sceneObject: SceneObject, ports: { pageEditing?: PageEditing }): SetTextOpacityResult | null {
    const refused = pageStateRefusal(sceneObject, ports, editTargetState, 'a text opacity');
    return refused ? nothingWritten([{ message: refused.message, code: refused.code }]) : null;
}

/** The alpha byte of a literal `#rrggbbaa` colour; 1 for any other colour. */
export function colorAlpha(color: unknown): number {
    return typeof color === 'string' && /^#[0-9A-Fa-f]{8}$/.test(color) ? Number.parseInt(color.slice(7), 16) / 255 : 1;
}

/** Whether a state or page can show `sceneObject`, so that a write must leave the others as they look. */
function showsStates(sceneObject: SceneObject, route: Route): boolean {
    return route.to !== 'base' || nearestStateAncestor(sceneObject) !== null;
}

/**
 * Set a Text's opacity, clamped to 0-1, where it is seen, as one undo step:
 * the state that shows on the text or its group, or, for shared content of a
 * Pages container while page editing is on, the showing page's state; else
 * Base. Every state and page keeps what it shows unless it is the one edited,
 * Base included: when one can show the text, the opacity is bound to a
 * `textOpacity` number property first, the states frozen to what they show,
 * and a group member is given its group's states.
 *
 * An unbound opacity over a `#rrggbbaa` colour takes the colour's alpha in
 * the same step, the colour becoming opaque, so the value written is the one
 * the text shows and every other context looks as it did.
 *
 * With page editing off, an edit into the state the showing page assigns is
 * refused with `STATE_ROUTED`. Edits on one object run in the order they
 * arrive; one whose route changed while it waited writes nothing.
 */
export async function setTextOpacityForState(
    sceneObject: SceneObject,
    value: number,
    options: SetTextOpacityOptions = {},
): Promise<SetTextOpacityResult> {
    const text = sceneObject.getBehavior(Text);
    if (!text) return nothingWritten([{ message: `No Text behavior on "${sceneObject.name}"`, code: 'NO_TEXT' }]);
    if (!Number.isFinite(value)) return nothingWritten([{ message: 'Text opacity must be a finite number', code: 'INVALID_VALUE' }]);
    const ports = { pageEditing: options.pageEditing };
    const refused = refusal(sceneObject, ports);
    if (refused) return refused;
    const route = routeOf(sceneObject, ports, editTargetState);
    if (showsStates(sceneObject, route) && stateEditRefused(sceneObject)) return nothingWritten();
    return queuePerObject(sceneObject, () => writeOpacity(sceneObject, text, Math.min(1, Math.max(0, value)), options, route));
}

async function writeOpacity(
    sceneObject: SceneObject,
    text: Text,
    value: number,
    options: SetTextOpacityOptions,
    requested: Route,
): Promise<SetTextOpacityResult> {
    const ports = { pageEditing: options.pageEditing };
    const refused = refusal(sceneObject, ports);
    if (refused) return refused;
    const route = routeOf(sceneObject, ports, editTargetState);
    if (!sameRoute(route, requested) || sceneObject.transform.isDisposed()) return nothingWritten();

    const commands: UndoCommand[] = [];
    const fail = async (errors: ValidationError[] = []): Promise<SetTextOpacityResult> => {
        for (const command of commands.reverse()) await command.undo({ silent: true });
        return nothingWritten(errors);
    };
    const label = `Set text opacity on ${sceneObject.name}`;

    const data = text.getData();
    const bound = boundPropertyName(data.opacity);
    const alpha = bound === null ? colorAlpha(data.color) : 1;
    let name = bound;
    let target: SetTextOpacityResult['target'];
    try {
        if (showsStates(sceneObject, route)) {
            const seed = alpha * (typeof data.opacity === 'number' ? data.opacity : 1);
            const kept = await keepStates(sceneObject, text, bound, seed, commands);
            if (typeof kept !== 'string') return await fail(kept);
            name = kept;
            // The bind awaited its commits: the value goes where the edit was routed, or nowhere.
            if (!sameRoute(routeOf(sceneObject, ports, editTargetState), route)) return await fail();
        }

        if (route.to === 'page' && name !== null) {
            const property = name;
            commands.push(await capturePageState(route.page, label, state => ({ ...state, values: { ...state.values, [property]: value } })));
            target = 'state';
        } else if (route.to === 'state' && name !== null) {
            const write = (await captureStateValue(sceneObject, name, value, { record: false })).command;
            if (write) commands.push(write);
            // A group member whose own states lack the group's showing one reports a capture
            // while the write is dropped: read it back.
            if (sceneObject.getBehavior(Properties)?.getData().states?.[route.state]?.values?.[name] !== value) {
                return await fail([{ message: `"${sceneObject.name}" has no state "${route.state}" of its own`, code: 'STATE_NOT_ON_OBJECT' }]);
            }
            target = 'state';
        } else if (name !== null) {
            const authored = await authorBoundValueInner(sceneObject, name, 'number', value, false, label);
            if (authored.errors.length > 0 || !authored.command) return await fail(authored.errors);
            commands.push(authored.command);
            target = 'property';
        } else {
            const before = text.getData();
            const errors = await text.commit({ opacity: value });
            if (errors.length > 0) return await fail(errors);
            commands.push(new BehaviorCommitCommand(sceneObject, 'Text', before, text.getData(), 'opacity'));
            target = 'text';
        }

        if (alpha < 1 && typeof data.color === 'string') {
            const before = text.getData();
            const errors = await text.commit({ color: data.color.slice(0, 7) });
            if (errors.length > 0) return await fail(errors);
            commands.push(new BehaviorCommitCommand(sceneObject, 'Text', before, text.getData(), null));
        }
    } catch (error) {
        return fail([{ message: error instanceof Error ? error.message : String(error), code: 'NOT_AUTHORABLE' }]);
    }

    const command = commands.length === 1 ? commands[0] : new CompositeCommand(label, commands);
    if (options.record !== false && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
    if (options.notify !== false) {
        const objectPath = sceneObject.getPath();
        dispatchBehaviorChanged({ objectPath, behaviorType: 'Text', action: 'updated' });
        if (target !== 'text') dispatchBehaviorChanged({ objectPath, behaviorType: 'Properties', action: 'updated' });
    }
    return { errors: [], target, propertyName: name, command, autoBound: bound === null && name !== null };
}

/**
 * Give every state on the text's own Properties a value for its opacity, the
 * one it shows now, so the write that follows changes one context only. An
 * unbound opacity is bound to a new `textOpacity` declaration of `seed`, with
 * the states frozen to it; a bound one has its states that carry no value
 * pinned to Base's. A group member is seeded with its group's states first.
 * Returns the property name, or the errors of a step that failed; each step
 * that ran is in `commands`.
 */
async function keepStates(
    sceneObject: SceneObject,
    text: Text,
    bound: string | null,
    seed: number,
    commands: UndoCommand[],
): Promise<string | ValidationError[]> {
    let props = sceneObject.getBehavior(Properties);
    if (!props) {
        props = await sceneObject.addBehavior(Properties);
        commands.push(new AddBehaviorCommand(sceneObject, 'Properties', props.getData()));
    }
    const seeded = await seedMemberStatesFromGroup(sceneObject, props, { record: false });
    if (seeded) commands.push(seeded);

    const before = props.getData();
    let name = bound;
    let next = before;
    if (name === null) {
        const taken = new Set(before.properties.map(d => d.name));
        name = PROPERTY;
        for (let i = 2; taken.has(name); i++) name = `${PROPERTY}${i}`;
        next = freezeStatesForDeclaration(before, bindingDeclaration('opacity', name, Math.min(1, Math.max(0, seed))));
    } else {
        const base = findPropertyProvider(sceneObject, name)?.properties.getProperty(name)?.value;
        for (const state of props.getStateNames()) {
            if (Object.hasOwn(next.states?.[state]?.values ?? {}, name)) continue;
            next = setStateValue(next, state, name, typeof base === 'number' ? base : 1);
        }
    }
    if (next !== before) {
        const errors = await props.commit({ properties: next.properties, states: next.states });
        if (errors.length > 0) return errors;
        commands.push(new BehaviorCommitCommand(sceneObject, 'Properties', before, props.getData(), null));
    }
    if (bound === null) {
        const textBefore = text.getData();
        const errors = await text.commit({ opacity: `$${name}` as unknown as number });
        if (errors.length > 0) return errors;
        commands.push(new BehaviorCommitCommand(sceneObject, 'Text', textBefore, text.getData(), null));
    }
    return name;
}
